import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { BehaviorSubject, map, Subject, switchMap } from 'rxjs';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxDataGridModule, DxLookupModule } from 'devextreme-angular';
import { NgIf, AsyncPipe } from '@angular/common';
import { LinkTransferService } from '../services/link-transfer.service';
import { showErrorSwal } from '@asamco/utils';
import { NameIdPair } from '../interfaces/name-id-pair.interface';
import { LinkTransferPreview } from '../interfaces/link-transfer-preview.interface';
import { DxiColumnModule, DxoFilterRowModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';
import { JsonEditorModalComponent } from '../json-editor-modal/json-editor-modal.component';

@Component({
  selector: 'app-shell-link-transfer-tool',
  templateUrl: './link-transfer-tool.component.html',
  styleUrls: ['../styles/alert.scss', '../styles/tile-style.scss', './link-transfer-tool.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, FormsModule, DxTemplateModule, AsyncPipe, DxLookupModule, DxoSelectionModule, DxoFilterRowModule, DxiColumnModule, DxDataGridModule, JsonEditorModalComponent]
})
export class LinkTransferToolComponent implements OnInit, OnDestroy {
  @ViewChild(JsonEditorModalComponent)
  jsonEditorModal: JsonEditorModalComponent;

  public sourceUsers$: BehaviorSubject<NameIdPair[]> = new BehaviorSubject<NameIdPair[]>(null);
  public linkTransferPreview$: BehaviorSubject<LinkTransferPreview[]> = new BehaviorSubject<LinkTransferPreview[]>(null);
  public targetCompanies$: BehaviorSubject<NameIdPair[]> = new BehaviorSubject<NameIdPair[]>(null);

  public sourceUserId: number;
  public targetCompanyId: number;

  sourceSystems;
  targetSystems = [{name: 'AL3 Test', id: 'Test'}, {name: 'AL3 UA', id: 'Acceptance'}, {name: 'AL3 Live', id: 'Live'}];

  sourceSystem = 'AL2';
  targetSystem = 'Test';

  loading = false;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef, private linkTransferService: LinkTransferService) {
    this.sourceSystems = [{name: 'AL2', id: 'AL2'}].concat(this.targetSystems);
  }

  ngOnInit(): void {
    this.linkTransferService.getSelectableAL3Companies(this.targetSystem).subscribe({ next: sc => this.targetCompanies$.next(sc), error: showErrorSwal });
    this.onSourceSystemChange({value: 'AL2'});
  }

  onSourceUserChange($event) {
    this.updatePreview();
  }

  onAL3CompanyChange($event) {
    this.updatePreview();
  }

  onTargetSystemChange($event) {
    this.linkTransferService.getSelectableAL3Companies($event.value).subscribe({ next: sc => this.targetCompanies$.next(sc), error: showErrorSwal });
  }

  onSourceSystemChange($event) {
    if($event.value === 'AL2') {
      this.linkTransferService.getSelectableAL2Users().subscribe({ next: sc => this.sourceUsers$.next(sc), error: showErrorSwal });
    } else {
      this.linkTransferService.getSelectableAL3Companies($event.value).subscribe({ next: sc => this.sourceUsers$.next(sc), error: showErrorSwal });
    }
  }

  updatePreview() {
    this.linkTransferPreview$.next(undefined);

    if (this.sourceUserId && this.targetCompanyId) {
      this.loading = true;
      if(this.sourceSystem === 'AL2') {
        this.linkTransferService.getAL2UserDomainPreview(this.sourceUserId).subscribe({ next: ud => {
          this.loading = false;
          this.linkTransferPreview$.next(ud);
        }, error: showErrorSwal });
      } else {
        this.linkTransferService.getDomainCompanyPreview(this.sourceUserId, this.sourceSystem).subscribe({ next: ud => {
          this.loading = false;
          this.linkTransferPreview$.next(ud);
        }, error: showErrorSwal });
      }
    }
  }

  transferLinks(linkIds: number[]) {
    this.loading = true;

    this.linkTransferService.prepareLinkTransferRequest({
      sourceSystem: this.sourceSystem,
      targetSystem: this.targetSystem,
      sourceLinkIds: linkIds || this.linkTransferPreview$.value.map(lp => lp.id),
      sourceUserId: this.sourceUserId,
      targetCompanyId: this.targetCompanyId
    }).pipe(switchMap(m => this.jsonEditorModal.showAndAwait(m.domainUser).pipe(map(du => ({...m, domainUser: du})))),
      switchMap(m => this.linkTransferService.transferLinks(m))).subscribe({
      next: r => {
        this.loading = false;

        const successes = r.map(rs => rs.successes).reduce((a, c) => a + c, 0);
        const total = r.map(rs => rs.successes + rs.failures).reduce((a, c) => a + c, 0);

        Swal.fire({
          icon: successes === total ? 'success' : 'warning',
          title: `Link transfer completed. Transfering ${successes}/${total} objects succeeded. See the attached file for detailed report.`
        });

        const a = <any>document.getElementById('linkanchor');
        const file = new Blob([JSON.stringify(r)], { type: 'application/json' });
        a.href = URL.createObjectURL(file);
        a.download = 'transferResults.json';
        a.dispatchEvent(new MouseEvent('click'));
      }, error: showErrorSwal, complete: () => this.loading = false
    });
  }

  updatePurchaseOrderCache($event) {
    this.linkTransferService.updatePurchaseOrderCache().subscribe(
      { next: (result: any) => Swal.fire({
        icon: 'success',
        title: 'Purchase Order Cache Updated',
        html: `<strong>Entries added successfully!</strong><br>
               <br>Previous count: <strong>${result.oldEntryCount}</strong>
               <br>New count: <strong>${result.newEntryCount}</strong>
               <br><br><strong>${result.newEntryCount - result.oldEntryCount} new entries</strong> added.`,
        confirmButtonText: 'OK'
    }),error: showErrorSwal });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
